import { Fragment } from "react";
import { ArrowUpRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/formatters";
import type { Hip3Market } from "@/types/hip3";
import { marketSlug } from "@/lib/market-utils";
import { useNavigate } from "react-router-dom";

export type DenseMarketGroup = {
  asset: string;
  quotes: string[];
  markets: Hip3Market[];
  totalVolume?: number;
  totalOpenInterest?: number;
};

type DenseMarketTableProps = {
  groupedMarkets: DenseMarketGroup[];
};

const numberCompact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const annualFundingFormat = (value?: number) =>
  value == null || Number.isNaN(value) ? "—" : `${(value * 100).toFixed(2)}%`;

const spreadFormat = (value?: number) =>
  value == null || Number.isNaN(value) ? "—" : `${(value / 100).toFixed(4)}%`;

const scoreFormat = (value?: number) =>
  value == null || Number.isNaN(value) ? "—" : value.toFixed(1);

export function DenseMarketTable({ groupedMarkets = [] }: DenseMarketTableProps) {
  const navigate = useNavigate();

  return (
    <section className="border border-border/80 bg-card/95 shadow-none">
      <div className="w-full min-w-0 overflow-x-auto">
        <table className="w-max min-w-full border-collapse">
          <thead className="bg-background/80 text-[0.7em] lg:text-xs uppercase text-muted-foreground font-mono tracking-wide">
            <tr className="border-b border-border/80">
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-left">Asset</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-left">Price</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">24h Volume</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Open Interest</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Funding (ann.)</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help border-b border-dotted border-muted-foreground/60">Spread (%)</span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-popover-foreground text-background/80 text-[0.7rem]">
                      <p>Estimated round-trip slippage as a percent of notional.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Score</th>
            </tr>
          </thead>
          <tbody>
            {!groupedMarkets.length && (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center text-sm text-muted-foreground">
                  No markets match the current filters.
                </td>
              </tr>
            )}

            {groupedMarkets.map((group) => {
              const providerCount = new Set(group.markets.map((market) => market.provider)).size;
              const canOpenAsset = providerCount > 1;

              return (
              <Fragment key={group.asset}>
                <tr
                  role={canOpenAsset ? "link" : undefined}
                  tabIndex={canOpenAsset ? 0 : undefined}
                  onClick={canOpenAsset ? () => navigate(`/assets/${group.asset}`) : undefined}
                  onKeyDown={
                    canOpenAsset
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            navigate(`/assets/${group.asset}`);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    "border-y border-border/80 bg-background/85 shadow-inner shadow-black/20",
                    canOpenAsset ? "cursor-pointer hover:bg-primary/5" : ""
                  )}
                >
                  <td colSpan={7} className="px-3 py-1.5 lg:px-5 lg:py-1.5">
                    <div className="flex flex-wrap items-center justify-between gap-3 text-xs lg:text-sm">
                      <div className="flex items-center gap-2 font-mono uppercase tracking-wide">
                        <span className={cn("inline-flex items-center gap-1", canOpenAsset ? "group" : "")}>
                          <span>{group.asset}</span>
                          {canOpenAsset ? (
                            <ArrowUpRight
                              className="size-3 text-muted-foreground transition-colors group-hover:text-primary"
                              aria-hidden="true"
                            />
                          ) : null}
                        </span>
                        <span className="text-muted-foreground/60">•</span>
                        <span className="text-muted-foreground/70">
                          {group?.markets[0]?.category ?? "General"}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-[0.65rem] lg:text-[0.75rem] uppercase font-mono text-muted-foreground">
                        <span className="rounded-full border border-border/70 bg-background/80 px-3 py-1">
                          {group.markets.length} markets
                        </span>
                        <span className="rounded-full border border-border/70 bg-background/80 px-3 py-1">
                          Vol ${numberCompact.format(group.totalVolume ?? 0)}
                        </span>
                        <span className="rounded-full border border-border/70 bg-background/80 px-3 py-1">
                          OI ${numberCompact.format(group.totalOpenInterest ?? 0)}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>

                {group.markets.map((market) => {
                  const slug = marketSlug(market);
                  const marketPath = `/markets/${market.provider}/${slug}`;

                  return (
                    <tr
                      key={`${market.provider}:${market.marketId}:${market.quoteAsset}`}
                      role="link"
                      tabIndex={0}
                      onClick={() => navigate(marketPath)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          navigate(marketPath);
                        }
                      }}
                      className={cn(
                        "group/market cursor-pointer border-b border-border/70 text-xs md:text-sm text-foreground/90 transition hover:bg-primary/5",
                        !market.isActive && "opacity-70"
                      )}
                    >
                      <td className="p-3 lg:px-5 lg:py-4">
                        <div className="flex flex-wrap items-center gap-1 md:gap-2">
                          <span className="whitespace-nowrap font-semibold transition-colors group-hover/market:text-primary">
                            <span>{market.baseAsset}</span>
                            <span className="text-muted-foreground/75 transition-colors group-hover/market:text-primary"> / {market.quoteAsset}</span>
                          </span>
                          <span className="inline-flex items-center rounded-full border border-border/70 bg-background/80 px-3 py-1 text-[0.62rem] uppercase text-muted-foreground transition group-hover/market:border-primary/50 group-hover/market:text-primary">
                            {market.provider}
                          </span>
                          <span className="rounded-full border border-border/70 bg-muted/20 px-3 py-1 text-[0.62rem] text-muted-foreground">
                            {market.maxLeverage ?? "—"}x
                          </span>
                        </div>
                      </td>
                      <td className="p-3 lg:px-5 lg:py-4 text-left font-mono text-muted-foreground">
                        ${formatPrice(market.currentPrice)}
                      </td>
                      <td className="p-3 lg:px-5 lg:py-4 text-right font-mono text-foreground">
                        ${numberCompact.format(market.volume24h ?? 0)}
                      </td>
                      <td className="p-3 lg:px-5 lg:py-4 text-right font-mono text-foreground">
                        ${numberCompact.format((market.openInterest ?? 0) * (market.currentPrice ?? 1))}
                      </td>
                      <td className="p-3 lg:px-5 lg:py-4 text-right font-mono text-foreground">
                        {annualFundingFormat(market.annualFunding)}
                      </td>
                      <td className="p-3 lg:px-5 lg:py-4 text-right font-mono text-foreground font-semibold">
                        {spreadFormat(market.spreadBps)}
                      </td>
                      <td
                        className={cn(
                          "p-3 lg:px-5 lg:py-4 text-right font-mono font-semibold",
                          market.marketScore != null && market.marketScore > 87.5 && "text-emerald-500",
                          market.marketScore != null && market.marketScore <= 87.5 && market.marketScore > 80 && "text-lime-500",
                          market.marketScore != null && market.marketScore <= 80 && market.marketScore >= 60 && "text-yellow-500",
                          market.marketScore != null && market.marketScore < 60 && "text-red-500"
                        )}
                      >
                        {scoreFormat(market.marketScore)}
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
